import { createSelector } from 'reselect';
import ms from 'milliseconds';
import shouldUpdate from '../reactors/should-update';
import { selectLoggedIn, selectAppTime } from './index';
import { AppState } from '../store';
import { SimpleListState } from '../reducers/simpleList';
import { selectPathname } from './router';
import {
  Account,
  Position,
  CashRestriction,
  Holdings,
  Order,
  Balance,
} from '../types/account';

export const selectAccountsRaw = (state: AppState) => state.accounts;

export const selectAccounts = createSelector(
  selectAccountsRaw,
  (rawAccounts) => {
    if (rawAccounts.data) {
      return rawAccounts.data;
    }
    return [];
  },
);

export const selectAccountsNeedData = createSelector(
  selectLoggedIn,
  selectAccountsRaw,
  selectAppTime,
  (loggedIn, rawAccounts, time) => {
    if (!loggedIn) {
      return false;
    }
    return shouldUpdate(rawAccounts, {
      staleTime: ms.minutes(10),
      now: time,
    });
  },
);

export const selectCashRestrictions = createSelector<
  AppState,
  Account[],
  CashRestriction[]
>(selectAccounts, (accounts) => {
  let restrictions: CashRestriction[] = [];
  accounts.map((account: Account) => {
    account.cash_restrictions.map((cashRestriction: CashRestriction) => {
      restrictions.push(cashRestriction);
      return null;
    });
    return null;
  });
  restrictions = restrictions.sort((a: CashRestriction, b: CashRestriction) =>
    a.account > b.account ? 1 : 0,
  );
  return restrictions;
});

export const selectAccountHoldings = (state: AppState) => state.accountHoldings;

export const selectAccountBalances = createSelector<
  AppState,
  SimpleListState<Holdings>,
  SimpleListState<Balance[]>
>(selectAccountHoldings, (holdings) => {
  let balances;
  if (holdings) {
    balances = Object.entries(holdings).reduce((balance: any, [key, val]) => {
      balance[key] = {
        data: val.data?.balances,
        error: val.error,
        lastError: val.lastError,
        lastFetch: val.lastFetch,
        loading: val.loading,
      };
      return balance;
    }, {});
  }
  return balances;
});

export const selectAccountPositions = createSelector<
  AppState,
  SimpleListState<Holdings>,
  SimpleListState<Position[]>
>(selectAccountHoldings, (holdings) => {
  let positions;
  if (holdings) {
    positions = Object.entries(holdings).reduce((position: any, [key, val]) => {
      position[key] = {
        data: val.data?.positions,
        error: val.error,
        lastError: val.lastError,
        lastFetch: val.lastFetch,
        loading: val.loading,
      };
      return position;
    }, {});
  }
  return positions;
});

export const selectAccountOrders = createSelector<
  AppState,
  SimpleListState<Holdings>,
  SimpleListState<Order[]>
>(selectAccountHoldings, (holdings) => {
  let orders;
  if (holdings) {
    orders = Object.entries(holdings).reduce((order: any, [key, val]) => {
      order[key] = {
        data: val.data?.orders,
        error: val.error,
        lastError: val.lastError,
        lastFetch: val.lastFetch,
        loading: val.loading,
      };
      return order;
    }, {});
  }
  return orders;
});

export const selectCurrentAccountId = createSelector<
  AppState,
  string,
  string | null
>(selectPathname, (pathname) => {
  let accountId = null;
  if (pathname && pathname.split('/').length >= 5) {
    accountId = pathname.split('/')[4];
  }
  return accountId;
});

export const selectCurrentAccountBalances = createSelector<
  AppState,
  string | null,
  SimpleListState<Balance[]>,
  Balance[] | null
>(selectCurrentAccountId, selectAccountBalances, (accountId, allBalances) => {
  let balances = null;
  if (
    accountId &&
    allBalances &&
    allBalances[accountId] &&
    allBalances[accountId].data
  ) {
    balances = allBalances[accountId].data;
  }
  return balances;
});

export const selectCurrentAccountPositions = createSelector<
  AppState,
  string | null,
  SimpleListState<Position[]>,
  Position[] | null
>(selectCurrentAccountId, selectAccountPositions, (accountId, allPositions) => {
  let positions = null;
  if (
    accountId &&
    allPositions &&
    allPositions[accountId] &&
    allPositions[accountId].data
  ) {
    positions = allPositions[accountId].data;
  }
  return positions;
});

export const selectCurrentAccountTotalValue = createSelector<
  AppState,
  string | null,
  SimpleListState<Holdings>,
  {
    total_value: number | null;
    currency: string | null;
  } | null
>(
  selectCurrentAccountId,
  selectAccountHoldings,
  (accountId, accountHoldings) => {
    if (
      accountId &&
      accountHoldings &&
      accountHoldings[accountId] &&
      accountHoldings[accountId].data
    ) {
      return {
        total_value:
          accountHoldings[accountId].data?.account?.balance?.total.amount ||
          null,
        currency:
          accountHoldings[accountId].data?.account?.balance?.total.currency ||
          null,
      };
    }
    return null;
  },
);

export const selectCurrentAccountPositionsError = createSelector<
  AppState,
  string | null,
  SimpleListState<Position[]>,
  boolean
>(selectCurrentAccountId, selectAccountPositions, (accountId, allPositions) => {
  let error = false;
  if (
    accountId &&
    allPositions &&
    allPositions[accountId] &&
    allPositions[accountId].lastError
  ) {
    error = true;
  }
  return error;
});

export const selectHiddenAccounts = createSelector<AppState, Account[], any[]>(
  selectAccounts,
  (accounts) => {
    let hiddenAccounts = [];
    hiddenAccounts = accounts.filter((a) => a.portfolio_group === null);
    return hiddenAccounts;
  },
);

export const selectHasOnlyCryptoAccounts = createSelector(
  selectAccounts,
  (accounts) => {
    return accounts.every((account) => account.meta.type === 'Crypto');
  },
);
